'use client'

import { useActionState } from 'react'
import { ZodSchema } from 'zod'

import { getFieldError, getFieldValue } from '../utils/form/form-utils'
import type { FormState, TypedFormAction, ZodFormData } from '../utils/form/types'

export type UseFormStateReturn<TSchema extends ZodSchema, TData = unknown> = {
  state: FormState<TData> | undefined
  formAction: (payload: FormData) => void
  isPending: boolean
  errors: Record<string, Array<string>>
  inputs: Record<string, unknown> | undefined
  message: string | undefined
  success: boolean
  getFieldError: (fieldName: keyof ZodFormData<TSchema>) => string | undefined
  getFieldValue: (fieldName: keyof ZodFormData<TSchema>) => string
}

export function useFormState<TSchema extends ZodSchema, TData = unknown>(
  action: TypedFormAction<TSchema, TData>,
): UseFormStateReturn<TSchema, TData> {
  const [state, formAction, isPending] = useActionState(action, undefined)

  const errors = state?.errors ?? {}
  const inputs = state?.inputs
  const message = state?.message
  const success = state?.success ?? false

  return {
    state,
    formAction,
    isPending,
    errors,
    inputs,
    message,
    success,
    getFieldError: (fieldName: keyof ZodFormData<TSchema>) => getFieldError(errors, fieldName),
    getFieldValue: (fieldName: keyof ZodFormData<TSchema>) => getFieldValue(inputs, fieldName),
  }
}
